'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import {
  enteredOnPasswordRecovery,
  forgetPasswordRecovery,
  rememberPasswordRecovery,
} from './password-recovery';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
  ) => Promise<{ error?: string; pendingConfirmation?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  /**
   * True while this visitor arrived from a "redefinir senha" e-mail and has not
   * chosen a new password yet. They have a session — Supabase grants one to open
   * the link — but it is not an ordinary sign-in, and letting them into the app
   * here is what left the old, forgotten password in place. See
   * `password-recovery.ts` for why this is tracked from two signals.
   */
  passwordRecovery: boolean;
  /** Writes the new password and, on success, ends the recovery state. */
  updatePassword: (password: string) => Promise<{ error?: string }>;
  /**
   * Changes the password of someone who is signed in and knows their current
   * one. `field` says which input the failure belongs to, so the view can point
   * at it without parsing English.
   */
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<{ error?: string; field?: 'current' | 'next' }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

/**
 * The invariant every auth action upholds: it resolves to `{ error? }`, it never
 * throws. supabase-js returns expected failures (bad password, taken e-mail) in
 * the `{ error }` field, but a hard transport failure — DNS/offline/CORS, or a
 * misconfigured Supabase URL — rejects the call instead. Left uncaught that
 * escapes as an unhandled rejection (the dev error overlay; a silent no-op in
 * prod). This turns any thrown value into a friendly, user-facing message.
 */
function messageFromThrown(e: unknown): string {
  if (e instanceof TypeError && /fetch/i.test(e.message)) {
    return 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
  }
  return e instanceof Error && e.message
    ? e.message
    : 'Ocorreu um erro inesperado. Tente novamente.';
}

/**
 * Ensures the signed-in user has a personal org (the "no org UI" model, D1).
 * `create_personal_org` is the idempotent RPC v1 already ships: it returns the
 * user's existing org or creates one. The daemon deliberately does not
 * provision in its auth path (it 403s an org-less account), so provisioning
 * happens here, once the user has a session. Fire-and-forget: a failure just
 * means the first scoped request 403s and the user can retry.
 */
async function ensurePersonalOrg(): Promise<void> {
  try {
    await supabase.rpc('create_personal_org');
  } catch {
    // Non-fatal — see the docblock.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Seeded from the URL this tab opened on, then also set by the event. Two
  // signals for one fact, because each alone loses a different race — the
  // reasoning lives in `password-recovery.ts`.
  const [passwordRecovery, setPasswordRecovery] = useState(enteredOnPasswordRecovery);
  // Provision at most once per user id per app load, so a token refresh or a
  // tab-focus re-emit does not fire a redundant RPC on every auth event.
  const provisionedFor = useRef<string | null>(null);

  useEffect(() => {
    // `loading` is what the gate spins on. Release it the moment ANY of these
    // resolve — never let a single stalled call hang the gate. getSession()
    // blocks on an internal token refresh when the stored token is expired; if
    // that refresh is slow, this fast path still unblocks with the on-disk
    // session via onAuthStateChange's INITIAL_SESSION.
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      setLoading(false);
    };
    const adopt = (s: Session | null) => {
      setSession(s);
      const uid = s?.user?.id ?? null;
      if (uid && provisionedFor.current !== uid) {
        provisionedFor.current = uid;
        void ensurePersonalOrg();
      }
      if (!uid) provisionedFor.current = null;
    };

    void supabase.auth.getSession().then(({ data }) => {
      adopt(data.session);
      release();
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // The other half of the recovery signal. supabase-js fires this from a
      // `setTimeout(..., 0)` scheduled before this subscription exists, so it
      // can arrive here or not arrive at all; when it does, it is authoritative.
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      adopt(s);
      release();
    });
    const failsafe = setTimeout(release, 8000);
    return () => {
      clearTimeout(failsafe);
      sub.subscription.unsubscribe();
    };
  }, []);

  // Persist from the state, not from the event: the URL is the other way this
  // becomes true, and persisting only on the event would lose exactly the case
  // the URL signal exists to catch. A reload must still owe a new password.
  useEffect(() => {
    if (passwordRecovery) rememberPasswordRecovery();
  }, [passwordRecovery]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signUp: async (email, password) => {
        // emailRedirectTo: the confirmation link returns to this app (must be
        // on the Supabase Auth Redirect URL allowlist for the v2 domain).
        try {
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { emailRedirectTo: `${window.location.origin}/` },
          });
          if (error) return { error: error.message };
          // With "Confirm email" on, signUp() creates the user but returns no
          // session until the confirmation link is clicked — the caller needs
          // to know this to show a "check your e-mail" state instead of
          // silently doing nothing (onAuthStateChange won't fire either).
          return { pendingConfirmation: data.session === null };
        } catch (e) {
          return { error: messageFromThrown(e) };
        }
      },
      signIn: async (email, password) => {
        try {
          const { error } = await supabase.auth.signInWithPassword({ email, password });
          return error ? { error: error.message } : {};
        } catch (e) {
          return { error: messageFromThrown(e) };
        }
      },
      resetPassword: async (email) => {
        try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/`,
          });
          return error ? { error: error.message } : {};
        } catch (e) {
          return { error: messageFromThrown(e) };
        }
      },
      passwordRecovery,
      updatePassword: async (password) => {
        try {
          const { error } = await supabase.auth.updateUser({ password });
          if (error) return { error: error.message };
          // Only now is the recovery actually over. Clearing this before the
          // write succeeded would drop the visitor into the app with the old
          // password still live — the exact bug this flow exists to close.
          setPasswordRecovery(false);
          forgetPasswordRecovery();
          return {};
        } catch (e) {
          return { error: messageFromThrown(e) };
        }
      },
      changePassword: async (currentPassword, newPassword) => {
        const email = session?.user?.email;
        // Mirrors Supabase's own wording for this condition rather than
        // inventing a parallel vocabulary, so one translation path covers it.
        // No `field`: nothing the person typed is at fault here.
        if (!email) return { error: 'Auth session missing!' };
        try {
          // Supabase does not ask for the current password: `updateUser` needs
          // only a session, and its "Secure password change" option is a
          // project-level setting that mails a nonce and does not even engage
          // while the session is under 24 hours old (auth-js 2.107.0,
          // GoTrueClient docblock at `updateUser`). So an unattended browser
          // would be enough to change the password and lock the owner out of a
          // paid account. Signing in again is what actually proves knowledge of
          // the current one.
          //
          // Safe against the obvious worry: on a wrong password this returns
          // `{ error }` without touching the stored session — it saves only on
          // success — so a typo here cannot sign anybody out. On success it
          // replaces the session with an equivalent, fresher one for the same
          // user, which `adopt` absorbs without re-provisioning.
          const { error: wrongCurrent } = await supabase.auth.signInWithPassword({
            email,
            password: currentPassword,
          });
          if (wrongCurrent) return { error: wrongCurrent.message, field: 'current' };

          const { error } = await supabase.auth.updateUser({ password: newPassword });
          if (error) return { error: error.message, field: 'next' };
          return {};
        } catch (e) {
          // A transport failure blames neither field.
          return { error: messageFromThrown(e) };
        }
      },
      signOut: async () => {
        // Leaving the recovery flag set would demand a new password again from
        // whoever signs in next in this same tab.
        setPasswordRecovery(false);
        forgetPasswordRecovery();
        await supabase.auth.signOut();
      },
    }),
    [session, loading, passwordRecovery],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The auth state when an `AuthProvider` is mounted (the hosted app), or null
 * (local / desktop, where there is no account). For screens shared by both.
 */
export function useOptionalAuth(): Pick<AuthState, 'user'> | null {
  return useContext(Ctx);
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
